import { ImgHTMLAttributes, useEffect, useRef, useState } from "react";
import { cn } from "utils/cn";

type FadeInImageProps = ImgHTMLAttributes<HTMLImageElement>;

/**
 * An `<img>` that eases in once the bitmap has actually arrived, so a late
 * image slides into place instead of snapping in halfway down a rendered page.
 * Cached images start opaque — they never flash — and a failed load reveals the
 * element anyway so `alt` text is not left invisible.
 */
export const FadeInImage = ({
  className,
  onLoad,
  onError,
  // Callers pass `alt` through; defaulting it keeps the decorative case honest
  // (an empty string) rather than rendering an img with no alt at all.
  alt = "",
  ...props
}: FadeInImageProps) => {
  const ref = useRef<HTMLImageElement>(null);
  const [shown, setShown] = useState(false);

  // A cached image can finish loading before React attaches `onLoad`, which
  // would strand it at opacity 0; `complete` on mount catches that case.
  useEffect(() => {
    if (ref.current?.complete) {
      setShown(true);
    }
  }, [props.src]);

  return (
    <img
      ref={ref}
      alt={alt}
      {...props}
      onLoad={(event) => {
        setShown(true);
        onLoad?.(event);
      }}
      onError={(event) => {
        setShown(true);
        onError?.(event);
      }}
      className={cn(
        "transition-opacity duration-300 ease-out",
        shown ? "opacity-100" : "opacity-0",
        className
      )}
    />
  );
};
